"use client";

import { useGestaoHoteleiraPage } from "./hooks/use-gestao-hoteleira-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  RecursosSection,
  //PorQueContratarSection,
} from "./components";
import { GenericInfoSection } from "@/features/shared/components/generic-info-section";
import { INFO_SECTIONS } from "@/features/shared/content/info-sections";

// Provas Sociais importadas da Home e UI
import { NumerosSection } from "@/features/home/components/numeros-section";
import { NaMidiaSection } from "@/features/home/components/na-midia-section";
import { SoftwareProductsCarousel, TrustedLogosMarquee, SmartIntegrationsTabs, WallOfLoveSection, DorParallaxSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";
// Import direto do arquivo `-lazy` (não do barrel `./components` da Home)
// pelo mesmo motivo já documentado em home-page.tsx: evita que o Rollup
// funda o wrapper lazy de volta no chunk da implementação real.
import { OtheoAiTeaserSection } from "@/features/home/components/otheo-ai-teaser-section-lazy";

import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

import { artigosMidia, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "Gestão Hoteleira (PMS)";

function GestaoHoteleiraPage() {
  useSeo({
    title: "Sistema de Gestão Hoteleira (PMS) | Foco Tecnologia",
    description:
      "PMS para hotéis e pousadas com controle total da operação: check-in, check-out, financeiro e housekeeping integrados em uma única plataforma na nuvem.",
    path: "/sistema-de-gestao-hoteleira-pms",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, isError, refetch } = useGestaoHoteleiraPage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Section 1: Hero */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-sistema-de-gestao-hoteleira-pms", title: LEAD_TITLE, icon: getProdutoIcone("/sistema-de-gestao-hoteleira-pms") })}
      />

      {/* Section 2: O que é um sistema PMS */}
      <GenericInfoSection {...INFO_SECTIONS.gestaoHoteleira} imageSide="right" />

      {/* Diagnóstico: prejuízos na gestão (mesmo cenário da home) */}
      <DorParallaxSection
        dorId="prejuizos"
        backgroundImage="/assets/imgs/gestao-hoteleira-pms/dor.webp"
        backgroundAlt="Hoteleiro estressado diante de um relatório financeiro com prejuízo operacional"
      />

      {/* Section 3: Recursos do PMS */}
      <RecursosSection />

      <OtheoAiTeaserSection />

      {/* Integrações Inteligentes */}
      <SmartIntegrationsTabs />

      {/* Section 4: Por que contratar */}
      {/*<PorQueContratarSection />*/}

      {/* Prova Social: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco otimizou suas operações."
      />

      {/* Prova Social: Na Mídia */}
      <NaMidiaSection artigos={artigosMidia} />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />
      <TrustedLogosMarquee />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="sistema-de-gestao-hoteleira-pms" />

      {/* Prova Social: FAQ */}
      <FAQAccordion
        items={[
          { id: "1", question: "O que é um PMS (Property Management System)?", answer: "Um PMS é um sistema de gestão hoteleira que centraliza todas as operações do hotel, incluindo reservas, check-in/check-out, controle financeiro, governança e muito mais em uma única plataforma." },
          { id: "2", question: "O PMS da Foco é adequado para qual tipo de propriedade?", answer: "Nosso PMS é ideal para hotéis, pousadas, resorts, hostels, flats e apart-hotéis e qualquer tipo de propriedade hoteleira que precise de uma gestão integrada e eficiente." },
          { id: "7", question: "O PMS da Foco é o mesmo tipo de sistema que um Property Management System internacional?", answer: "Sim. Property Management System, Hotel Management System e Hotel Management Software são os nomes usados no mercado internacional para o mesmo tipo de sistema que a Foco oferece: uma plataforma única para gestão de reservas, tarifas, check-in/check-out e financeiro do seu hotel, pousada ou rede." },
          { id: "6", question: "PMS, programa de gestão e software de gestão hoteleira são a mesma coisa?", answer: "São nomes diferentes para o mesmo tipo de sistema. Hoteleiros e donos de pousada costumam pesquisar por \"programa para hotel\", \"programa para pousada\" ou \"sistema PMS\" — na Foco, é a mesma plataforma de gestão hoteleira, que centraliza reservas, tarifas, check-in/check-out e financeiro, seja para hotel, pousada, resort ou hostel." },
          { id: "3", question: "O sistema funciona na nuvem?", answer: "Sim! O PMS da Foco é 100% baseado em nuvem, permitindo acesso de qualquer lugar e a qualquer momento, com total segurança dos seus dados." },
          { id: "4", question: "O PMS integra com outros sistemas?", answer: "Sim, oferecemos integração com Channel Manager, Motor de Reservas, sistemas de pagamento, contabilidade e muitos outros parceiros do ecossistema hoteleiro." },
          { id: "5", question: "Como funciona o suporte técnico?", answer: "Oferecemos suporte técnico 24/7 via chat, e-mail e telefone, além de uma base de conhecimento completa e treinamentos para sua equipe." },
        ]}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o Sistema PMS"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para transformar a gestão do seu hotel?"
        subtitle="Solicite uma demonstração e descubra como o PMS da Foco pode otimizar todas as operações do seu negócio."
        badge="Comece agora"
        source="sistema-de-gestao-hoteleira-pms"
        leadTitle={LEAD_TITLE}
      />
    </div>
  );
}

export { GestaoHoteleiraPage };
